/**
 * Pure, dependency-free helpers for the inquiry detail view.
 *
 * Kept in their own module (no React / next-intl imports) so tests can import
 * the REAL logic without pulling the component's provider graph into bun's
 * process-global `mock.module` cache (the codebase's testing convention).
 */

import type { ClientIdentityRevealLevel } from '@/types/professionals-types';

// ==========================================================================
// SHARED DOCUMENT PROBE — #6203 structured status signal
// ==========================================================================

/**
 * Discriminated-union result from the shared-document download-url probe.
 *
 * The backend emits three meaningful HTTP statuses for
 * ``GET /matches/{id}/shared-document/download-url``:
 *
 *   200 → document is available; body contains ``signed_url`` + ``expires_at``
 *   404 → no document was uploaded for this match
 *   410 → document existed but has expired / been purged
 *
 * Using a typed discriminant prevents callers from branching on error-message
 * wording, which is locale-dependent and breaks whenever the backend message
 * changes (#6203).
 */
export type SharedDocumentProbeResult =
  | {
      status: 'available';
      signed_url: string;
      expires_at: string;
      original_filename: string;
    }
  | { status: 'expired' }
  | { status: 'not_found' }
  | { status: 'error' };

/**
 * Map the raw service response + optional HTTP status to a typed
 * ``SharedDocumentProbeResult``.
 *
 * The HTTP status (when available — callers obtain it from
 * ``axios.isAxiosError(err) && err.response?.status``) is the primary
 * discriminant for 410 vs 404.  When the status is absent (network error,
 * unexpected shape), we fall through to the ``'error'`` branch.
 *
 * @param httpStatus  The HTTP response code, or ``undefined`` when the caller
 *                    cannot recover it (e.g. network-level failure).
 * @param body        The raw response body (resolved by the axios interceptor).
 */
export function classifySharedDocumentProbe(
  httpStatus: number | undefined,
  body: unknown
): SharedDocumentProbeResult {
  if (httpStatus === 410) return { status: 'expired' };
  if (httpStatus === 404) return { status: 'not_found' };

  // 200-path: the body has a signed_url.
  if (
    body !== null &&
    typeof body === 'object' &&
    'signed_url' in body &&
    typeof (body as Record<string, unknown>)['signed_url'] === 'string'
  ) {
    const b = body as {
      signed_url: string;
      expires_at: string;
      original_filename: string;
    };
    return {
      status: 'available',
      signed_url: b.signed_url,
      expires_at: b.expires_at,
      original_filename: b.original_filename,
    };
  }

  return { status: 'error' };
}

export type InquiryViewerSide = 'client' | 'professional';

/**
 * Pre-contractual conflict-of-interest note gate (#6142). Show the note only to
 * the ADVOCATE while the client is still anonymous — i.e. the graded reveal
 * level (#6038) is 'none'. Once the client reveals at least 'name' the
 * pre-contractual phase is over and the note disappears. The client never sees
 * it. A missing/undefined level is treated as the privacy-preserving default
 * ('none' → still anonymous).
 */
export function shouldShowPreContractualNote(args: {
  viewerSide: InquiryViewerSide;
  clientIdentityRevealed: ClientIdentityRevealLevel | null | undefined;
}): boolean {
  const level = args.clientIdentityRevealed ?? 'none';
  return args.viewerSide === 'professional' && level === 'none';
}

/**
 * Consented intake-summary read gate (#5835 / #5959). Returns the trimmed
 * summary to render, or `null` to render nothing.
 *
 * The summary is only persisted when the client consented (#5835's double gate),
 * so a present value is by construction shareable; an erased one (#5958) arrives
 * null and disappears. The `.trim() || null` collapses null / undefined / empty /
 * whitespace-only into a single "render nothing" signal so an un-shared (or
 * blanked) summary never surfaces an empty labelled card to the advocate.
 */
export function resolveSharedIntakeSummary(
  sharedIntakeSummary: string | null | undefined
): string | null {
  return sharedIntakeSummary?.trim() || null;
}
